import { Logger } from '_common/services';
import { Command } from '../../Command';
import { JsonRange } from 'Editor/services/_Common/Selection';
import { NodeUtils } from 'Editor/services/DataManager';

export class ToggleListCommand extends Command {
  private options: Editor.Edition.StylesCommandOptions;
  private rangeToApply?: Editor.Selection.JsonRange;

  private listStyleId: string | boolean;

  constructor(
    context: Editor.Edition.Context,
    listStyleId: string | boolean = false,
    rangeToApply?: Editor.Selection.JsonRange,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context);

    this.listStyleId = listStyleId;
    this.rangeToApply = rangeToApply;

    this.options = {
      untrackedActionWarning: true,
      createPatch: true,
      ...options,
    };
  }

  private async removeList(blockId: string) {
    if (this.context.DataManager?.numbering.isListElement(blockId)) {
      await this.context.DataManager.numbering.removeBlocksFromList([blockId]);
    }

    return true;
  }

  private async applyList(
    range: Editor.Selection.JsonRange,
    baseModel: Editor.Data.Node.Model,
    blockData: Editor.Data.Node.Data,
    blockPath: Editor.Selection.Path,
  ) {
    if (this.listStyleId == null || typeof this.listStyleId !== 'string') {
      return false;
    }

    if (
      !this.context.DataManager ||
      !this.context.stylesManager ||
      !this.context.contentManipulator ||
      !NodeUtils.isParagraphData(blockData) ||
      !blockData.id
    ) {
      return false;
    }

    const listStyleToApply = this.context.DataManager.styles.listStyles.style(this.listStyleId);

    if (!listStyleToApply) {
      return false;
    }

    let previousListId: string | undefined;
    let previousListStyleId: string | undefined | null;

    // get previous sibling block
    let previousBaseModel: Editor.Data.Node.Model | undefined;
    let previousBlockData: Editor.Data.Node.Data | undefined | null;

    if (baseModel.id === blockData.id) {
      previousBaseModel = this.context.DataManager.nodes.getPreviousModelById(baseModel.id);
      previousBlockData = previousBaseModel?.selectedData();
    } else {
      const baseData = baseModel.selectedData();
      if (baseData) {
        const previousDataInfo = NodeUtils.getPreviousSibling(baseData, blockPath);

        previousBaseModel = baseModel;
        previousBlockData = previousDataInfo?.data;
      }
    }

    if (NodeUtils.isParagraphData(previousBlockData) && previousBlockData.id) {
      previousListId =
        this.context.DataManager.numbering.getListIdFromBlock(previousBlockData.id) || '';
      previousListStyleId = this.context.DataManager.numbering.getStyleIdForList(previousListId);
    }

    if (listStyleToApply.isMultiLevelList()) {
      // MULTI-LEVEL LISTS WITH STYLES

      // remove from list first if is a list element
      if (
        this.context.DataManager.numbering.isListElement(blockData.id) &&
        !this.context.DataManager.numbering.isBlockInOutlineList(blockData.id)
      ) {
        this.removeList(blockData.id);
      }

      // search for document outline list
      const listsWithStyles = this.context.DataManager.styles.documentStyles.getListsWithStyles();

      //TODO: TempFix rethink about multiple outlines
      let outlineListId = listsWithStyles[0];

      if (outlineListId) {
        // there's already an outline from document

        const outlineListStyleId =
          this.context.DataManager.numbering.getStyleIdForList(outlineListId);

        if (!this.context.DataManager.numbering.isListInDocument(outlineListId)) {
          // create a new outline in the document
          outlineListId = this.context.DataManager.numbering.createNewList(
            this.listStyleId,
            outlineListId,
          );
        }

        if (!outlineListStyleId || this.listStyleId !== outlineListStyleId) {
          // update outline list with new style
          this.context.DataManager.numbering.setListStyle(outlineListId, this.listStyleId);
        }

        // update document styles
        this.context.DataManager.styles.updateDocumentStylesForList(
          outlineListId,
          this.listStyleId,
        );

        // apply document style to block element
        const styleToApply = listStyleToApply.level(
          listStyleToApply.getLevelForType(blockData.properties.s),
        ).et;

        if (styleToApply) {
          await this.context.stylesManager.documentStyles.applyDocumentStyleToSelection(
            styleToApply,
            range,
            {
              applySelection: false,
              createPatch: false,
              resetParagraphStyle: false,
              untrackedActionWarning: false,
            },
          );
        }
      } else {
        // create a new outline
        outlineListId = this.context.DataManager.numbering.createNewList(this.listStyleId);

        // update document styles
        this.context.DataManager.styles.updateDocumentStylesForList(
          outlineListId,
          this.listStyleId,
        );

        // apply document style to block element
        const styleToApply = listStyleToApply.level(
          listStyleToApply.getLevelForType(blockData.properties.s),
        ).et;

        if (styleToApply) {
          await this.context.stylesManager.documentStyles.applyDocumentStyleToSelection(
            styleToApply,
            range,
            {
              applySelection: false,
              createPatch: false,
              resetParagraphStyle: false,
              untrackedActionWarning: false,
            },
          );
        }
      }
    } else {
      // SIMPLE LISTS

      // if theres no previous list or style is diferent creates a new list id
      let listId = previousListId;
      if (!listId || this.listStyleId !== previousListStyleId) {
        listId = this.context.DataManager.numbering.createNewList(this.listStyleId);
      }

      // get level if element is already a list
      const level = this.context.DataManager.numbering.getListLevelFromBlock(blockData.id);

      // remove from list first if is a list element
      this.removeList(blockData.id);

      this.context.DataManager.numbering.addBlocksToList(
        [blockData.id],
        listId,
        level || 0,
        previousBlockData?.id,
      );
    }
  }

  private async toogleListToRange(ctx: Editor.Edition.ActionContext) {
    if (
      !this.context.DataManager ||
      !this.context.stylesManager ||
      !this.context.contentManipulator
    ) {
      return false;
    }

    const blockRangesData = JsonRange.splitRangeByTypes(
      this.context.DataManager,
      ctx.range,
      ['p'],
      {
        sameContainerLevel: this.options.sameContainerLevel || false,
        useSelectedCells: this.options.useSelectedCells || true,
      },
    );

    for (let i = 0; i < blockRangesData.length; i++) {
      const range = blockRangesData[i].range;
      const blockData = blockRangesData[i].filteredData;
      const blockPath = blockRangesData[i].filteredDataPath;

      const baseModel = this.context.DataManager.nodes.getNodeModelById(range.start.b);

      // check if element is editable
      if (!baseModel || !this.context.DataManager.nodes.isNodeEditable(baseModel.id)) {
        continue;
      }

      if (blockData.id) {
        if (
          this.listStyleId != null &&
          this.listStyleId != '' &&
          typeof this.listStyleId === 'string'
        ) {
          // apply list
          await this.applyList(range, baseModel, blockData, blockPath);
        } else {
          // remove list
          await this.removeList(blockData.id);
        }
      }
    }

    return true;
  }

  protected async handleExec(...args: any[]): Promise<void> {
    if (this.context.debug) {
      Logger.trace('ToggleListCommand exec', this);
    }

    if (!this.context.DataManager || !this.context.stylesManager) {
      return;
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext(this.rangeToApply);

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    if (await this.toogleListToRange(this.actionContext)) {
      //trigger seelction check
      this.checkSelectionStatus();

      if (this.options.createPatch) {
        this.createPatch();
      }
    }
  }
}
